import React, { useState } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem, SectionShell } from "../components/Reveal";
import { CONTACT_INFO, IMAGES } from "../content";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", request_type: "contact" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent. Thank you!");
      setForm({ name: "", email: "", subject: "", message: "", request_type: "contact" });
    } catch {
      toast.error("Could not send right now — please email contact@destinycs.com directly.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_18px_rgba(0,240,255,0.08)] transition-all";

  return (
    <main data-testid="contact-page">
      <PageHero
        overline="CONTACT US"
        title="Let's connect."
        highlight="We'd love to hear from you."
        desc="Tell us about your project, the geography you need, or the dataset you're after — a geospatial expert will respond."
        image={IMAGES.city_lights}
      />

      <SectionShell className="pt-4">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10">
          <Stagger className="space-y-4">
            <StaggerItem>
              <div className="border border-white/10 rounded-2xl bg-[#111622]/70 p-7" data-testid="contact-address-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                    <MapPin size={17} className="text-cyan-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">Address</h3>
                </div>
                {CONTACT_INFO.address.map((line) => (
                  <p key={line} className="text-sm text-slate-400 leading-relaxed">{line}</p>
                ))}
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="border border-white/10 rounded-2xl bg-[#111622]/70 p-7" data-testid="contact-phone-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                    <Phone size={17} className="text-cyan-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">Phone</h3>
                </div>
                {CONTACT_INFO.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/[^0-9+]/g, "")}`} data-testid={`contact-phone-${p.replace(/[^0-9]/g, "")}`} className="block font-mono-tech text-sm text-slate-300 hover:text-cyan-400 transition-colors py-0.5">
                    {p}
                  </a>
                ))}
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="border border-white/10 rounded-2xl bg-[#111622]/70 p-7" data-testid="contact-email-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                    <Mail size={17} className="text-cyan-400" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">Email</h3>
                </div>
                <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email-link" className="text-sm text-slate-300 hover:text-cyan-400 transition-colors">
                  {CONTACT_INFO.email}
                </a>
              </div>
            </StaggerItem>
          </Stagger>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-white/10 rounded-3xl bg-[#111622]/70 backdrop-blur-md p-8 sm:p-10"
            >
              <h2 className="font-display text-2xl font-extrabold text-white tracking-tight">Leave a message</h2>
              <p className="mt-2 text-sm text-slate-500">Fields marked required only — we reply within one business day.</p>
              <div className="mt-7 grid sm:grid-cols-2 gap-4">
                <input
                  required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                  className={field}
                />
                <input
                  required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="contact-email-input"
                  className={field}
                />
              </div>
              <div className="mt-4">
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Subject — e.g. POI data for Southeast Asia"
                  data-testid="contact-subject-input"
                  className={field}
                />
              </div>
              <div className="mt-4">
                <textarea
                  required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your project or data requirement…"
                  data-testid="contact-message-input"
                  className={`${field} resize-none`}
                />
              </div>
              <button
                type="submit" disabled={sending}
                data-testid="contact-submit-button"
                className="mt-7 w-full sm:w-auto flex items-center justify-center gap-2 bg-cyan-400 text-[#05070A] font-semibold px-8 py-3.5 rounded-full hover:bg-cyan-300 hover:shadow-[0_0_32px_rgba(0,240,255,0.4)] transition-all disabled:opacity-50"
              >
                <Send size={15} /> {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </SectionShell>
    </main>
  );
}
